/**
 * Age Estimation Module for AppBouncer SDK
 * Handles ONNX-based age prediction from face images
 */

import * as ort from 'onnxruntime-web/wasm';

// ============================================================
// Types
// ============================================================

export interface AgeModelConfig {
  /** Path to the ONNX age model (default: /models/age_fp16.onnx) */
  modelPath: string;
  /** Model input size (default: 224) */
  inputSize: number;
  /** ONNX execution provider (default: 'wasm') */
  executionProvider: 'wasm' | 'webgpu' | 'webgl';
  /** Age bucket labels (for classification models) */
  ageBuckets?: string[];
  /** Threshold index for 18+ detection (default: 2) */
  adultThresholdIndex?: number;
}

export interface AgeEstimationResult {
  /** Estimated age (continuous value or bucket midpoint) */
  age: number;
  /** Confidence score (0-1) */
  confidence: number;
  /** Age bucket label (if using classification model) */
  bucketLabel?: string;
  /** Bucket index */
  bucketIndex?: number;
  /** Probability distribution across buckets */
  probabilities?: number[];
  /** Whether estimated age is >= 18 */
  isAdult: boolean;
}

export interface AgeHistory {
  /** Array of age estimates */
  estimates: number[];
  /** Rolling average */
  average: number;
  /** Maximum history size */
  maxSize: number;
}

export interface MultiEstimateConfig {
  /** Number of estimates to run (default: 4) */
  numEstimates?: number;
  /** Delay between estimates in ms (default: 600) */
  delayBetweenMs?: number;
  /** Minimum confidence threshold to include estimate (default: 0.6) */
  confidenceThreshold?: number;
  /** Progress callback (current estimate number, total estimates) */
  onProgress?: (current: number, total: number) => void;
  /** Capture image on each estimate (default: false) */
  captureImages?: boolean;
}

export interface AgeEstimateWithImage {
  /** Age estimate result */
  result: AgeEstimationResult;
  /** Captured image as data URL (JPEG) */
  imageDataUrl: string | null;
}

export interface MultiEstimateResult extends AgeEstimationResult {
  /** Image closest to the average age (for upload) */
  bestImage: string | null;
  /** All captured images with their estimates */
  allEstimates?: AgeEstimateWithImage[];
}

// ============================================================
// Default Configuration
// ============================================================

export const DEFAULT_AGE_CONFIG: AgeModelConfig = {
  modelPath: './models/age_fp16.onnx', // Relative to HTML file
  inputSize: 224,
  executionProvider: 'wasm',
  ageBuckets: ['0-2', '4-6', '8-12', '15-20', '25-32', '38-43', '48-53', '60-100'],
  adultThresholdIndex: 2, // index >= this means >=18
};

// Age bucket midpoints for continuous estimation
const BUCKET_MIDPOINTS = [1, 5, 10, 17.5, 28.5, 40.5, 50.5, 80];

// ============================================================
// Age Estimation Class
// ============================================================

export class AgeEstimator {
  private config: AgeModelConfig;
  private session: ort.InferenceSession | null = null;
  private isInitialized = false;
  private isDemoMode = false;
  private history: AgeHistory;

  constructor(config: Partial<AgeModelConfig> = {}) {
    this.config = { ...DEFAULT_AGE_CONFIG, ...config };
    this.history = {
      estimates: [],
      average: 0,
      maxSize: 21,
    };
  }

  /**
   * Check if estimator is in demo mode (model failed to load)
   */
  isInDemoMode(): boolean {
    return this.isDemoMode;
  }

  /**
   * Get current execution provider
   */
  getExecutionProvider(): string {
    return this.isDemoMode ? 'demo' : this.config.executionProvider;
  }

  /**
   * Initialize the ONNX model
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      // Configure WASM files to load from CDN (required for bundled usage)
      // Use version 1.16.3 to match the installed package version
      ort.env.wasm.wasmPaths = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.23.2/dist/';

      // Disable multi-threading to avoid loading threaded WASM files
      // This simplifies CDN loading and works in more environments
      ort.env.wasm.numThreads = 1;

      // Use only WASM backend (more reliable than WebGL/WebGPU for CDN usage)
      this.session = await ort.InferenceSession.create(this.config.modelPath, {
        executionProviders: ['wasm'],
      });

      this.isInitialized = true;
    } catch (error) {
      console.warn('Age model failed to load, falling back to demo mode:', error);
      this.isDemoMode = true;
      this.isInitialized = true;
    }
  }

  /**
   * Preprocess image data for the model
   * Converts RGBA ImageData to CHW float32 tensor (0-1 range)
   */
  private preprocessImage(imageData: ImageData): Float32Array {
    const { data, width, height } = imageData;
    const inputSize = this.config.inputSize;

    // Create canvas for resizing if needed
    let pixelData = data;
    if (width !== inputSize || height !== inputSize) {
      const canvas = document.createElement('canvas');
      canvas.width = inputSize;
      canvas.height = inputSize;
      const ctx = canvas.getContext('2d')!;

      // Create temporary canvas with original data
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = width;
      tempCanvas.height = height;
      const tempCtx = tempCanvas.getContext('2d')!;
      tempCtx.putImageData(imageData, 0, 0);

      // Draw scaled
      ctx.drawImage(tempCanvas, 0, 0, inputSize, inputSize);
      pixelData = ctx.getImageData(0, 0, inputSize, inputSize).data;
    }

    // Convert to CHW format (channels first) with 0-1 normalization
    const chw = new Float32Array(3 * inputSize * inputSize);
    let p = 0;

    // Red channel
    for (let i = 0; i < inputSize * inputSize; i++) {
      chw[p++] = pixelData[i * 4] / 255;
    }
    // Green channel
    for (let i = 0; i < inputSize * inputSize; i++) {
      chw[p++] = pixelData[i * 4 + 1] / 255;
    }
    // Blue channel
    for (let i = 0; i < inputSize * inputSize; i++) {
      chw[p++] = pixelData[i * 4 + 2] / 255;
    }

    return chw;
  }

  /**
   * Crop face from video/canvas to model input size
   */
  cropFaceRegion(
    source: HTMLVideoElement | HTMLCanvasElement,
    landmarks: Array<{ x: number; y: number }>,
    padding = 0.25
  ): HTMLCanvasElement {
    const inputSize = this.config.inputSize;

    // Get source dimensions
    const sourceWidth = source instanceof HTMLVideoElement ? source.videoWidth : source.width;
    const sourceHeight = source instanceof HTMLVideoElement ? source.videoHeight : source.height;

    // Calculate bounding box from landmarks
    const xs = landmarks.map((p) => p.x * sourceWidth);
    const ys = landmarks.map((p) => p.y * sourceHeight);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    // Calculate padded square region
    const w = maxX - minX;
    const h = maxY - minY;
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const size = Math.max(w, h) * (1 + padding);

    // Clamp to source bounds
    const x = Math.max(0, cx - size / 2);
    const y = Math.max(0, cy - size / 2);
    const s = Math.min(size, Math.min(sourceWidth - x, sourceHeight - y));

    // Create output canvas
    const canvas = document.createElement('canvas');
    canvas.width = inputSize;
    canvas.height = inputSize;
    const ctx = canvas.getContext('2d')!;

    // Draw cropped and scaled face
    ctx.drawImage(source, x, y, s, s, 0, 0, inputSize, inputSize);

    return canvas;
  }

  /**
   * Apply softmax to logits
   */
  private softmax(logits: Float32Array | number[]): number[] {
    const maxLogit = Math.max(...logits);
    const exps = Array.from(logits).map((v) => Math.exp(v - maxLogit));
    const sum = exps.reduce((a, b) => a + b, 0);
    return exps.map((v) => v / sum);
  }

  /**
   * Run age estimation on cropped face image
   */
  async estimate(faceCanvas: HTMLCanvasElement): Promise<AgeEstimationResult> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    // Demo mode fallback
    if (this.isDemoMode || !this.session) {
      return this.getDemoResult();
    }

    const inputSize = this.config.inputSize;
    const ctx = faceCanvas.getContext('2d')!;
    const imageData = ctx.getImageData(0, 0, faceCanvas.width, faceCanvas.height);

    // Preprocess
    const inputTensor = this.preprocessImage(imageData);
    const input = new ort.Tensor('float32', inputTensor, [1, 3, inputSize, inputSize]);

    // Run inference
    const results = await this.session.run({ images: input });
    const outputName = Object.keys(results)[0];
    const logits = results[outputName].data as Float32Array;

    // Process output based on model type
    if (logits.length === 1) {
      // Regression model - direct age output
      const age = logits[0];
      return {
        age,
        confidence: 0.9,
        isAdult: age >= 18,
      };
    } else {
      // Classification model - bucket probabilities
      const probs = this.softmax(logits);

      // Find best bucket
      let bestIndex = 0;
      let bestProb = 0;
      probs.forEach((p, i) => {
        if (p > bestProb) {
          bestProb = p;
          bestIndex = i;
        }
      });

      // Calculate expected age from probabilities
      let expectedAge = 0;
      probs.forEach((p, i) => {
        if (i < BUCKET_MIDPOINTS.length) {
          expectedAge += p * BUCKET_MIDPOINTS[i];
        }
      });

      const buckets = this.config.ageBuckets || DEFAULT_AGE_CONFIG.ageBuckets!;
      const adultThreshold = this.config.adultThresholdIndex ?? 2;

      return {
        age: expectedAge,
        confidence: bestProb,
        bucketLabel: buckets[bestIndex] || `bucket_${bestIndex}`,
        bucketIndex: bestIndex,
        probabilities: probs,
        isAdult: bestIndex >= adultThreshold,
      };
    }
  }

  /**
   * Estimate age from video element with landmarks
   */
  async estimateFromVideo(
    video: HTMLVideoElement,
    landmarks: Array<{ x: number; y: number }>,
    padding = 0.25
  ): Promise<AgeEstimationResult> {
    const faceCanvas = this.cropFaceRegion(video, landmarks, padding);
    return this.estimate(faceCanvas);
  }

  /**
   * Run multiple age estimates and return averaged result
   * Takes 4-5 seconds with default settings (8 estimates × 500ms delay)
   * Optionally captures images on each estimate for best-image selection
   */
  async estimateAgeMultiple(
    faceCanvas: HTMLCanvasElement,
    config: MultiEstimateConfig = {}
  ): Promise<MultiEstimateResult> {
    const {
      numEstimates = 4,
      delayBetweenMs = 600,
      confidenceThreshold = 0.6,
      onProgress,
      captureImages = true, // Default to capturing images
    } = config;

    const estimatesWithImages: AgeEstimateWithImage[] = [];

    for (let i = 0; i < numEstimates; i++) {
      // Report progress
      onProgress?.(i + 1, numEstimates);

      // Run single estimate
      const result = await this.estimate(faceCanvas);

      // Capture image if enabled
      let imageDataUrl: string | null = null;
      if (captureImages) {
        imageDataUrl = faceCanvas.toDataURL('image/jpeg', 0.85);
      }

      // Filter by confidence threshold (skip low-confidence results)
      if (result.confidence >= confidenceThreshold) {
        estimatesWithImages.push({ result, imageDataUrl });
      }

      // Delay between estimates (except after last one)
      if (i < numEstimates - 1) {
        await new Promise((r) => setTimeout(r, delayBetweenMs));
      }
    }

    // Average the filtered estimates and select best image
    return this.averageEstimatesWithImages(estimatesWithImages);
  }

  /**
   * Run multiple estimates from video with landmarks
   */
  async estimateFromVideoMultiple(
    video: HTMLVideoElement,
    landmarks: Array<{ x: number; y: number }>,
    config: MultiEstimateConfig = {},
    padding = 0.25
  ): Promise<MultiEstimateResult> {
    const faceCanvas = this.cropFaceRegion(video, landmarks, padding);
    return this.estimateAgeMultiple(faceCanvas, config);
  }

  /**
   * Average multiple age estimation results (legacy - without images)
   */
  private averageEstimates(estimates: AgeEstimationResult[]): AgeEstimationResult {
    // Fallback if all estimates were filtered out
    if (estimates.length === 0) {
      console.warn('All estimates filtered due to low confidence, using demo result');
      return this.getDemoResult();
    }

    // Calculate averages
    const avgAge = estimates.reduce((sum, e) => sum + e.age, 0) / estimates.length;
    const avgConfidence = estimates.reduce((sum, e) => sum + e.confidence, 0) / estimates.length;

    // Get age bucket for averaged age
    const bucketIndex = this.getAgeBucketIndex(avgAge);
    const buckets = this.config.ageBuckets || DEFAULT_AGE_CONFIG.ageBuckets!;
    const adultThreshold = this.config.adultThresholdIndex ?? 2;

    return {
      age: Math.round(avgAge * 10) / 10, // Round to 1 decimal
      confidence: Math.round(avgConfidence * 100) / 100,
      bucketLabel: buckets[bucketIndex] || `bucket_${bucketIndex}`,
      bucketIndex,
      probabilities: estimates[0]?.probabilities, // Use first estimate's distribution
      isAdult: bucketIndex >= adultThreshold,
    };
  }

  /**
   * Average estimates and select the image closest to the average age
   */
  private averageEstimatesWithImages(
    estimatesWithImages: AgeEstimateWithImage[]
  ): MultiEstimateResult {
    // Fallback if all estimates were filtered out
    if (estimatesWithImages.length === 0) {
      console.warn('All estimates filtered due to low confidence, using demo result');
      const demoResult = this.getDemoResult();
      return {
        ...demoResult,
        bestImage: null,
      };
    }

    // Calculate averages
    const avgAge =
      estimatesWithImages.reduce((sum, e) => sum + e.result.age, 0) / estimatesWithImages.length;
    const avgConfidence =
      estimatesWithImages.reduce((sum, e) => sum + e.result.confidence, 0) /
      estimatesWithImages.length;

    // Find the estimate closest to the average age
    let closestIndex = 0;
    let closestDiff = Math.abs(estimatesWithImages[0].result.age - avgAge);
    for (let i = 1; i < estimatesWithImages.length; i++) {
      const diff = Math.abs(estimatesWithImages[i].result.age - avgAge);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIndex = i;
      }
    }

    const bestImage = estimatesWithImages[closestIndex].imageDataUrl;

    // Get age bucket for averaged age
    const bucketIndex = this.getAgeBucketIndex(avgAge);
    const buckets = this.config.ageBuckets || DEFAULT_AGE_CONFIG.ageBuckets!;
    const adultThreshold = this.config.adultThresholdIndex ?? 2;

    return {
      age: Math.round(avgAge * 10) / 10, // Round to 1 decimal
      confidence: Math.round(avgConfidence * 100) / 100,
      bucketLabel: buckets[bucketIndex] || `bucket_${bucketIndex}`,
      bucketIndex,
      probabilities: estimatesWithImages[0]?.result.probabilities,
      isAdult: bucketIndex >= adultThreshold,
      bestImage,
      allEstimates: estimatesWithImages,
    };
  }

  /**
   * Get age bucket index from continuous age value
   */
  private getAgeBucketIndex(age: number): number {
    // Bucket boundaries: 0-2, 4-6, 8-12, 15-20, 25-32, 38-43, 48-53, 60-100
    const boundaries = [3, 7, 13, 22, 35, 45, 55, 100];
    for (let i = 0; i < boundaries.length; i++) {
      if (age <= boundaries[i]) return i;
    }
    return boundaries.length - 1;
  }

  /**
   * Demo mode result
   */
  private getDemoResult(): AgeEstimationResult {
    // Return a plausible demo result
    const demoAge = 25 + Math.random() * 5;
    return {
      age: demoAge,
      confidence: 0.5,
      bucketLabel: '25-32',
      bucketIndex: 4,
      probabilities: [0.01, 0.02, 0.05, 0.12, 0.6, 0.15, 0.04, 0.01],
      isAdult: true,
    };
  }

  /**
   * Add estimate to history and get rolling average
   */
  addToHistory(estimate: number): AgeHistory {
    this.history.estimates.push(estimate);

    // Trim to max size
    if (this.history.estimates.length > this.history.maxSize) {
      this.history.estimates.shift();
    }

    // Calculate average
    this.history.average =
      this.history.estimates.reduce((a, b) => a + b, 0) / this.history.estimates.length;

    return { ...this.history };
  }

  /**
   * Clear history
   */
  clearHistory(): void {
    this.history.estimates = [];
    this.history.average = 0;
  }

  /**
   * Get current history
   */
  getHistory(): AgeHistory {
    return { ...this.history };
  }

  /**
   * Get progress percentage (based on history size)
   */
  getProgress(): number {
    return Math.min(100, (this.history.estimates.length / this.history.maxSize) * 100);
  }

  /**
   * Dispose resources
   */
  dispose(): void {
    // ONNX session doesn't have a dispose method, but we clear references
    this.session = null;
    this.isInitialized = false;
  }
}

// ============================================================
// Overlay Drawing Utilities
// ============================================================

export interface OverlayConfig {
  /** Ellipse radius X as fraction of canvas size (default: 0.30) */
  ellipseRadiusX: number;
  /** Ellipse radius Y as fraction of canvas size (default: 0.40) */
  ellipseRadiusY: number;
  /** Dimmed overlay color (default: rgba(0, 0, 0, 0.7)) */
  overlayColor: string;
  /** Border color when not highlighted (default: #00ffcc) */
  borderColor: string;
  /** Border color when highlighted (default: #00ff99) */
  highlightColor: string;
  /** Progress ring color (default: #00ff99) */
  progressColor: string;
  /** Border line width (default: 2) */
  borderWidth: number;
  /** Progress ring line width (default: 8) */
  progressWidth: number;
  /** Font for labels (default: 20px sans-serif) */
  labelFont: string;
  /** Font for prompts (default: 18px sans-serif) */
  promptFont: string;
}

export const DEFAULT_OVERLAY_CONFIG: OverlayConfig = {
  ellipseRadiusX: 0.3,
  ellipseRadiusY: 0.4,
  overlayColor: 'rgba(0, 0, 0, 0.7)',
  borderColor: '#00ffcc',
  highlightColor: '#00ff99',
  progressColor: '#00ff99',
  borderWidth: 2,
  progressWidth: 8,
  labelFont: '20px sans-serif',
  promptFont: '18px sans-serif',
};

/**
 * Draw face oval overlay with optional progress ring
 */
export function drawFaceOverlay(
  ctx: CanvasRenderingContext2D,
  options: {
    highlight?: boolean;
    ageLabel?: string | null;
    progress?: number | null;
    promptText?: string;
    config?: Partial<OverlayConfig>;
  } = {}
): void {
  const cfg = { ...DEFAULT_OVERLAY_CONFIG, ...options.config };
  const { highlight = false, ageLabel = null, progress = null, promptText = '' } = options;

  const w = ctx.canvas.width;
  const h = ctx.canvas.height;
  const size = Math.min(w, h);

  const centerX = w / 2;
  const centerY = h / 2;
  const radiusX = size * cfg.ellipseRadiusX;
  const radiusY = size * cfg.ellipseRadiusY;

  // Clear canvas
  ctx.clearRect(0, 0, w, h);

  // Dimmed overlay
  ctx.fillStyle = cfg.overlayColor;
  ctx.fillRect(0, 0, w, h);

  // Oval mask (clear the ellipse area)
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
  ctx.clip();
  ctx.clearRect(centerX - radiusX - 5, centerY - radiusY - 5, radiusX * 2 + 10, radiusY * 2 + 10);
  ctx.restore();

  // Border
  ctx.beginPath();
  ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
  ctx.strokeStyle = highlight ? cfg.highlightColor : cfg.borderColor;
  ctx.lineWidth = cfg.borderWidth;
  ctx.stroke();

  // Age label
  if (ageLabel) {
    ctx.font = cfg.labelFont;
    ctx.fillStyle = cfg.borderColor;
    ctx.textAlign = 'center';
    ctx.fillText(`Age: ${ageLabel}`, centerX, centerY - radiusY - 15);
  }

  // Progress ring
  if (progress !== null && progress >= 0 && progress <= 100) {
    const progressAngle = (progress / 100) * 2 * Math.PI;
    const startAngle = -Math.PI / 2;
    const endAngle = startAngle + progressAngle;

    ctx.beginPath();
    ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, startAngle, endAngle);
    ctx.strokeStyle = cfg.progressColor;
    ctx.lineWidth = cfg.progressWidth;
    ctx.stroke();
  }

  // Prompt text
  if (promptText) {
    ctx.font = cfg.promptFont;
    ctx.fillStyle = '#fff';
    ctx.textAlign = 'center';
    ctx.shadowColor = '#222';
    ctx.shadowBlur = 4;
    ctx.fillText(promptText, w / 2, h - 24);
    ctx.shadowBlur = 0;
  }
}

/**
 * Draw landmarks on canvas
 */
export function drawLandmarks(
  ctx: CanvasRenderingContext2D,
  landmarks: Array<{ x: number; y: number }>,
  color = 'rgba(0, 255, 0, 0.8)',
  radius = 1.5
): void {
  const w = ctx.canvas.width;
  const h = ctx.canvas.height;

  ctx.fillStyle = color;
  for (const lm of landmarks) {
    const x = lm.x * w;
    const y = lm.y * h;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * Draw face bounding box
 */
export function drawFaceBounds(
  ctx: CanvasRenderingContext2D,
  landmarks: Array<{ x: number; y: number }>,
  color = 'rgba(0, 255, 0, 0.8)',
  lineWidth = 2
): void {
  const w = ctx.canvas.width;
  const h = ctx.canvas.height;

  const xs = landmarks.map((p) => p.x * w);
  const ys = landmarks.map((p) => p.y * h);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.strokeRect(minX, minY, maxX - minX, maxY - minY);
}

// ============================================================
// Factory Function
// ============================================================

/**
 * Create an age estimator with optional configuration
 */
export function createAgeEstimator(config?: Partial<AgeModelConfig>): AgeEstimator {
  return new AgeEstimator(config);
}
